import React, { useState, useCallback, useMemo } from 'react';
import AddQuestionModal from './components/UpdateEmail';
import { IFetchEmailFilters, useFetchEmail, useUpdateEmailAndSend, useBulkSendEmails } from '../../../hooks/admin/email/email';
import { toast } from 'react-toastify';

export interface Question {
  id?: number;
  question: string;
  answer: string;
  category: string;
  status: 'Pending' | 'Approved' | 'Rejected';
  source: string;
  lastUpdated: string;
  email?: string;
}

export interface Email {
  id: number;
  sender_email: string;
  message: string;
  ai_generated_reply: string;
  subject: string;
  sent_at: string | null;
  is_approved: boolean;
  created_at: string;
  status?: string; // Added status field for filtering
}

interface EmailApiResponse {
  page: number;
  page_size: number;
  total_emails: number;
  emails: Email[];
}

interface BulkSendRequest {
  email_ids: number[];
}

const EmailManagement: React.FC = () => {
  const [emailFilters, setEmailFilters] = useState<IFetchEmailFilters>({
    is_approved: false,
    page: "1",
    page_size: "10",
    search: "",
  });

  const [activeQuestion, setActiveQuestion] = useState<Question>();
  const [showModal, setShowModal] = useState(false);
  const [localSearch, setLocalSearch] = useState("");
  const [selectedEmails, setSelectedEmails] = useState<number[]>([]);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all'); // Added status filter state

  // API hooks
  const { 
    data: emailData, 
    isLoading, 
    error, 
    refetch 
  } = useFetchEmail(emailFilters);
  
  const sendMailAPI = useUpdateEmailAndSend();
  const bulkSendAPI = useBulkSendEmails();

  // Memoized email data with proper typing
  const emails: Email[] = useMemo(() => 
    emailData?.emails || [], 
    [emailData?.emails]
  );

  // Filter emails based on local search AND status filter
  const filteredEmails = useMemo(() => {
    let filtered = emails;

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(email => {
        switch (statusFilter) {
          case 'pending':
            return !email.is_approved && !email.sent_at;
          case 'approved':
            return email.is_approved && !email.sent_at;
          case 'sent':
            return email.sent_at !== null;
          case 'unprocessed':
            return !email.is_approved && !email.sent_at; // Same as pending
          case 'matched':
            return email.is_approved && !email.sent_at; // Same as approved
          default:
            return true;
        }
      });
    }

    // Apply local search
    if (localSearch.trim()) {
      const searchLower = localSearch.toLowerCase();
      filtered = filtered.filter(email => 
        email.sender_email.toLowerCase().includes(searchLower) ||
        email.message.toLowerCase().includes(searchLower) ||
        email.subject.toLowerCase().includes(searchLower) ||
        (email.ai_generated_reply && email.ai_generated_reply.toLowerCase().includes(searchLower))
      );
    }

    return filtered;
  }, [emails, localSearch, statusFilter]);

  // Calculate pagination info
  const paginationInfo = useMemo(() => {
    const currentPage = parseInt(emailFilters.page || "1");
    const pageSize = parseInt(emailFilters.page_size || "10");
    const totalEmails = emailData?.total_emails || 0;
    const startItem = (currentPage - 1) * pageSize + 1;
    const endItem = Math.min(currentPage * pageSize, totalEmails);
    
    return {
      currentPage,
      pageSize,
      totalEmails,
      startItem,
      endItem,
      hasNext: emailData ? currentPage * pageSize < totalEmails : false,
      hasPrev: currentPage > 1
    };
  }, [emailData, emailFilters.page, emailFilters.page_size]);

  // Check if all emails on current page are selected
  const allEmailsSelected = useMemo(() => {
    if (filteredEmails.length === 0) return false;
    return filteredEmails.every(email => selectedEmails.includes(email.id));
  }, [filteredEmails, selectedEmails]);

  // Check if any emails are selected
  const hasSelectedEmails = useMemo(() => {
    return selectedEmails.length > 0;
  }, [selectedEmails]);

  // Get status counts for filter display
  const statusCounts = useMemo(() => {
    const counts = {
      all: emails.length,
      pending: emails.filter(email => !email.is_approved && !email.sent_at).length,
      approved: emails.filter(email => email.is_approved && !email.sent_at).length,
      sent: emails.filter(email => email.sent_at !== null).length,
      unprocessed: emails.filter(email => !email.is_approved && !email.sent_at).length,
      matched: emails.filter(email => email.is_approved && !email.sent_at).length,
    };
    return counts;
  }, [emails]);

  // Get status display text and color
  const getStatusInfo = useCallback((email: Email) => {
    if (email.sent_at) {
      return { text: 'Sent', color: 'bg-green-100 text-green-800' };
    } else if (email.is_approved) {
      return { text: 'Approved', color: 'bg-blue-100 text-blue-800' };
    } else {
      return { text: 'Pending', color: 'bg-yellow-100 text-yellow-800' };
    }
  }, []);

  // Handlers
  const handleSendMail = useCallback((email: Email) => {
    toast.dismiss();
    
    if (!email.ai_generated_reply?.trim()) {
      toast.error("Cannot send empty reply");
      return;
    }

    toast.loading("Sending mail...");

    sendMailAPI.mutate({
      id: email.id,
      is_updated: false,
      new_reply: email.ai_generated_reply,
      status:"sent"
    }, {
      onSuccess: () => {
        toast.dismiss();
        toast.success("Mail sent successfully");
        refetch(); // Refresh the data
      },
      onError: (error) => {
        toast.dismiss();
        console.error("Send mail error:", error);
        toast.error("Unable to send mail. Please try again.");
      }
    });
  }, [sendMailAPI, refetch]);

  const handleBulkSend = useCallback(() => {
    toast.dismiss();
    
    if (selectedEmails.length === 0) {
      toast.error("No emails selected");
      return;
    }

    // Validate that all selected emails have replies
    const emailsWithoutReplies = filteredEmails.filter(
      email => selectedEmails.includes(email.id) && !email.ai_generated_reply?.trim()
    );

    if (emailsWithoutReplies.length > 0) {
      toast.error(`Cannot send ${emailsWithoutReplies.length} email(s) with empty replies`);
      return;
    }

    toast.loading(`Sending ${selectedEmails.length} email(s)...`);

    bulkSendAPI.mutate({
      email_ids: selectedEmails,
    }, {
      onSuccess: () => {
        toast.dismiss();
        toast.success(`Successfully sent ${selectedEmails.length} email(s)`);
        setSelectedEmails([]); // Clear selection
        setIsAllSelected(false);
        refetch(); // Refresh the data
      },
      onError: (error) => {
        toast.dismiss();
        console.error("Bulk send error:", error);
        toast.error("Unable to send emails. Please try again.");
      }
    });
  }, [selectedEmails, filteredEmails, bulkSendAPI, refetch]);

  const handleEditQuestion = useCallback((email: Email) => {
    setActiveQuestion({
      question: email.message,
      answer: email.ai_generated_reply || '',
      category: email.subject,
      status: email.is_approved ? "Approved" : "Pending",
      source: "Email",
      lastUpdated: email.created_at,
      email: email.sender_email,
      id: email.id
    });
    setShowModal(true);
  }, []);

  const closeEditModal = useCallback(() => {
    setActiveQuestion(undefined);
    setShowModal(false);
  }, []);

  const handleSaveQuestion = useCallback((question: Question) => {
    console.log("Saving question:", question);
    // Implement save logic here
    toast.success("Question updated successfully");
    closeEditModal();
    refetch(); // Refresh data after save
  }, [closeEditModal, refetch]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalSearch(e.target.value);
  }, []);

  const handleStatusFilterChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setEmailFilters(prev => ({ 
      ...prev, 
      is_approved: e.target.value === 'true',
      page: "1" // Reset to first page when filter changes
    }));
    setSelectedEmails([]); // Clear selection when filter changes
    setIsAllSelected(false);
  }, []);

  // New status filter handler
  const handleEmailStatusFilterChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value);
    setSelectedEmails([]); // Clear selection when filter changes
    setIsAllSelected(false);
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setEmailFilters(prev => ({
      ...prev,
      page: newPage.toString()
    }));
    setSelectedEmails([]); // Clear selection when page changes
    setIsAllSelected(false);
  }, []);

  const handlePageSizeChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setEmailFilters(prev => ({
      ...prev,
      page_size: e.target.value,
      page: "1" // Reset to first page when page size changes
    }));
    setSelectedEmails([]); // Clear selection when page size changes
    setIsAllSelected(false);
  }, []);

  // Selection handlers
  const handleSelectEmail = useCallback((emailId: number) => {
    setSelectedEmails(prev => {
      if (prev.includes(emailId)) {
        return prev.filter(id => id !== emailId);
      } else {
        return [...prev, emailId];
      }
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (allEmailsSelected) {
      // Deselect all
      setSelectedEmails([]);
    } else {
      // Select all visible emails
      const allVisibleIds = filteredEmails.map(email => email.id);
      setSelectedEmails(allVisibleIds);
    }
  }, [allEmailsSelected, filteredEmails]);

  const handleBulkReply = useCallback(() => {
    // For bulk reply, you might want to open a modal where you can edit a common reply
    // that will be applied to all selected emails, or edit them individually
    toast.info("Bulk reply functionality - implement based on your requirements");
    // You could open a modal here to set a common reply for all selected emails
  }, []);

  // Format date for display
  const formatDate = useCallback((dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  // Loading and error states
  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-bold text-red-600 mb-2">Error Loading Emails</h2>
          <p className="text-gray-600 mb-4">Unable to load email data. Please try again.</p>
          <button 
            onClick={() => refetch()}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 min-h-screen font-sans">
      <div className="container mx-auto p-6">
        <div className="bg-white shadow rounded-lg p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Email Management</h2>
              <p className="text-gray-600 mt-1">
                Manage and respond to incoming emails ({emailData?.total_emails || 0} total)
                {hasSelectedEmails && ` - ${selectedEmails.length} selected`}
                {statusFilter !== 'all' && ` - Filtered: ${statusFilter} (${filteredEmails.length})`}
              </p>
            </div>
          </div>

          {/* Bulk Actions Bar */}
          {hasSelectedEmails && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="text-blue-800 font-medium">
                  {selectedEmails.length} email(s) selected
                </div>
                <div className="flex gap-3 flex-wrap">
                  <button
                    onClick={handleBulkSend}
                    disabled={bulkSendAPI.isLoading}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                  >
                    📧 Send Selected ({selectedEmails.length})
                  </button>
                  <button
                    onClick={() => setSelectedEmails([])}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    Clear Selection
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center mb-6">
            <div className="relative flex-1 max-w-md">
              <input
                type="text"
                placeholder="Search emails, questions, or replies..."
                value={localSearch}
                onChange={handleSearchChange}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 pl-10 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                🔍
              </div>
            </div>
            
            <div className="flex gap-4 flex-wrap">
              {/* Status Filter */}
              <select 
                value={statusFilter}
                onChange={handleEmailStatusFilterChange}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Status ({statusCounts.all})</option>
                <option value="pending">Pending ({statusCounts.pending})</option>
                <option value="approved">Approved ({statusCounts.approved})</option>
                <option value="sent">Sent ({statusCounts.sent})</option>
                <option value="unprocessed">Unprocessed ({statusCounts.unprocessed})</option>
                <option value="matched">Matched ({statusCounts.matched})</option>
              </select>

              {/* Approval Filter */}
              <select 
                value={emailFilters.is_approved?.toString()}
                onChange={handleStatusFilterChange}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="false">Pending Approval</option>
                <option value="true">Approved Only</option>
              </select>

              {/* Page Size */}
              <select 
                value={emailFilters.page_size}
                onChange={handlePageSizeChange}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="10">10 per page</option>
                <option value="25">25 per page</option>
                <option value="50">50 per page</option>
              </select>
            </div>
          </div>

          {/* Email Table */}
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                    <input
                      type="checkbox"
                      checked={allEmailsSelected}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Question
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Intent 
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    AI Generated Reply
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Subject
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Time Received
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading ? (
                  // Loading state
                  Array.from({ length: 5 }).map((_, index) => (
                    <tr key={index} className="animate-pulse">
                      <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-4"></div></td>
                      <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-3/4"></div></td>
                      <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded"></div></td>
                      <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-5/6"></div></td>
                      <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-1/2"></div></td>
                      <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-2/3"></div></td>
                      <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-2/3"></div></td>
                      <td className="px-6 py-4"><div className="h-6 bg-gray-200 rounded w-20"></div></td>
                    </tr>
                  ))
                ) : filteredEmails.length === 0 ? (
                  // Empty state
                  <tr>
                    <td colSpan={8} className="px-6 py-8 text-center text-gray-500">
                      {localSearch || statusFilter !== 'all' 
                        ? 'No emails match your current filters.' 
                        : 'No emails found.'}
                    </td>
                  </tr>
                ) : (
                  // Email data
                  filteredEmails.map((email) => {
                    const statusInfo = getStatusInfo(email);
                    return (
                      <tr 
                        key={email.id} 
                        className={`hover:bg-gray-50 transition-colors duration-150 ${
                          selectedEmails.includes(email.id) ? 'bg-blue-50' : ''
                        }`}
                      >
                        <td className="px-6 py-4">
                          <input
                            type="checkbox"
                            checked={selectedEmails.includes(email.id)}
                            onChange={() => handleSelectEmail(email.id)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          <div className="flex flex-col">
                            <span className="font-medium">{email.sender_email}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                          <div className="line-clamp-3">{email.message}</div>
                        </td>
                         <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                          <div className="line-clamp-3">{email.subject}</div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700 max-w-md">
                          <div className="line-clamp-3">{email.ai_generated_reply || 'No reply generated'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {email.subject}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
                            {statusInfo.text}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(email.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-3">
                            <button
                              onClick={() => handleSendMail(email)}
                              disabled={sendMailAPI.isLoading || email.sent_at !== null}
                              className={`p-2 rounded-lg transition-colors ${
                                email.sent_at !== null
                                  ? 'text-gray-400 cursor-not-allowed'
                                  : 'text-green-600 hover:text-green-900 hover:bg-green-50'
                              }`}
                              title={email.sent_at !== null ? "Email already sent" : "Send email"}
                            >
                              📧
                            </button>
                            <button
                              onClick={() => handleEditQuestion(email)}
                              className="p-2 rounded-lg text-blue-600 hover:text-blue-900 hover:bg-blue-50 transition-colors"
                              title="Edit reply"
                            >
                              ✏️
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-600">
              Showing {paginationInfo.startItem}-{paginationInfo.endItem} of {paginationInfo.totalEmails} emails
              {hasSelectedEmails && ` (${selectedEmails.length} selected)`}
              {statusFilter !== 'all' && ` - Filtered: ${statusFilter}`}
            </p>
            
            <div className="flex space-x-2">
              <button
                onClick={() => handlePageChange(paginationInfo.currentPage - 1)}
                disabled={!paginationInfo.hasPrev || isLoading}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              <span className="px-4 py-2 text-sm text-gray-700">
                Page {paginationInfo.currentPage}
              </span>
              <button
                onClick={() => handlePageChange(paginationInfo.currentPage + 1)}
                disabled={!paginationInfo.hasNext || isLoading}
                className="px-4 py-2 bg-blue-500 border border-transparent rounded-lg text-sm font-medium text-white hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      <AddQuestionModal 
        isOpen={showModal} 
        onSave={handleSaveQuestion} 
        onClose={closeEditModal} 
        initialData={activeQuestion}
      />
    </div>
  );
};

export default EmailManagement;